import React, { useEffect, useMemo, useState } from 'react'
import Table from 'react-bootstrap/Table'

import { MONTH_NAMES } from 'js/utils/constants'

import Loader from '../Loader'
import { useSearchParams } from 'react-router-dom'
import {
  ControlRunMonthsParams,
  getControlRunMonths,
} from 'js/services/controlRuns'
import { ControlRunMonthData } from 'js/models'
import { TimeRange } from './ControlRunsTableWrapper'
import { Button } from 'react-bootstrap'

export default function ControlRunMonthsTable(props: {
  timeRange: TimeRange
  orgId?: number | null
  setShowMonthList: (bool: boolean) => void
  setYear: (val: number | null) => void
  setMonth: (val: number | null) => void
  updateOrgParam: boolean
}): React.ReactElement {
  const {
    timeRange,
    orgId,
    setShowMonthList,
    setYear,
    setMonth,
    updateOrgParam,
  } = props
  const [, setSearchParams] = useSearchParams()
  const [awaitingResponse, setAwaitingResponse] = useState(true)
  const [monthsWithData, setMonthsWithData] = useState<ControlRunMonthData[]>()

  const params: ControlRunMonthsParams = useMemo(() => {
    let startDate: Date

    switch (timeRange) {
      case TimeRange.lastTwelveMonths:
        startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)
        break

      case TimeRange.lastTwoYears:
        startDate = new Date()
        startDate.setFullYear(startDate.getFullYear() - 2)
        break

      case TimeRange.allTime:
        startDate = new Date(0)
        break
    }

    return {
      startMonth: startDate.getMonth() + 1,
      startYear: startDate.getFullYear(),
      ...(orgId && { organization: orgId }),
    }
  }, [timeRange, orgId])

  useEffect(() => {
    setAwaitingResponse(true)
    if (orgId === undefined) return
    getControlRunMonths(params)
      .then(data => {
        setMonthsWithData(data)
      })
      .finally(() => {
        setAwaitingResponse(false)
      })
  }, [orgId, timeRange])

  const renderTableRow = (
    monthData: ControlRunMonthData
  ): React.ReactElement => {
    return (
      <tr
        key={`control-run-month-row-${monthData.datetimeYear}-${monthData.datetimeMonth}`}
        className='clickable'
        onClick={(): void => {
          setSearchParams({
            year: monthData.datetimeYear.toString(),
            month: monthData.datetimeMonth.toString(),
            ...(orgId && updateOrgParam
              ? { organization: orgId.toString() }
              : {}),
          })
          setYear(monthData.datetimeYear)
          setMonth(monthData.datetimeMonth)
          setShowMonthList(false)
        }}
      >
        <td>
          {MONTH_NAMES[monthData.datetimeMonth]} {monthData.datetimeYear}
        </td>
        <td>{monthData.count}</td>
      </tr>
    )
  }

  if (!monthsWithData) return <Loader />

  return (
    <>
      <Button
        onClick={(): void => {
          const y = new Date().getFullYear()
          const m = new Date().getMonth() + 1
          setSearchParams({
            year: y.toString(),
            month: m.toString(),
            ...(orgId && updateOrgParam
              ? { organization: orgId.toString() }
              : {}),
          })
          setYear(y)
          setMonth(m)
          setShowMonthList(false)
        }}
        variant='secondary'
        size='sm'
        active={false}
        style={{ marginBottom: '1rem' }}
      >
        Go to Current Month
      </Button>
      {awaitingResponse ? (
        <Loader />
      ) : monthsWithData.length == 0 ? (
        <p className='m-0'>No control runs for the selected time range</p>
      ) : (
        <Table hover className='table-header-primary'>
          <thead>
            <tr>
              <th>Months with Data</th>
              <th># Data Points</th>
            </tr>
          </thead>
          <tbody>{monthsWithData.map(m => renderTableRow(m))}</tbody>
        </Table>
      )}
    </>
  )
}
